import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { enhancedApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Pagination } from '../components/Pagination';
import { 
  FileText, 
  Search, 
  Filter, 
  Calendar, 
  Clock, 
  AlertCircle, 
  CheckCircle, 
  Eye, 
  Edit, 
  Play,
  RefreshCw,
  TrendingUp,
  TrendingDown,
  User,
  Calendar as CalendarIcon,
  Flag,
  BarChart3,
  Activity,
  Users,
  X
} from 'lucide-react';

interface MyAudit {
  id: number;
  case_type: 'mortality' | 'morbidity';
  case_id: number;
  patient_id: string;
  patient_name?: string;
  status: 'assigned' | 'in_progress' | 'submitted' | 'under_review' | 'approved' | 'rejected' | 'needs_revision';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assigned_date: string;
  due_date: string;
  progress_percentage: number;
  case_summary?: string;
  primary_diagnosis?: string;
  department_name?: string;
  unit_name?: string;
  supervisor_name?: string; // API returns supervisor_name, not reviewer_name
  notes?: string;
  days_remaining?: number;
  is_overdue: boolean;
  // Multi-auditor fields
  assignment_type?: string;
  multi_auditor_support?: boolean;
  user_role?: string;
  multi_auditor_info?: string;
  // Revision tracking fields
  revision_count?: number;
  last_revision_date?: string;
  assigned_to_id?: number;
  assigned_to_name?: string;
}

interface AuditStats {
  total: number;
  assigned: number;
  in_progress: number;
  submitted: number;
  approved: number;
  overdue: number;
}

export const MyAudits: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [audits, setAudits] = useState<MyAudit[]>([]);
  const [stats, setStats] = useState<AuditStats>({
    total: 0,
    assigned: 0,
    in_progress: 0,
    submitted: 0,
    approved: 0,
    overdue: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [priorityFilter, setPriorityFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('due_date');
  const [selectedAudit, setSelectedAudit] = useState<MyAudit | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    fetchMyAudits();
  }, []);

  // Refetch audits when pagination changes
  useEffect(() => {
    fetchMyAudits();
  }, [currentPage, itemsPerPage]);

  const fetchMyAudits = async () => {
    try {
      setLoading(true);
      const response = await enhancedApi.audit.getMyAudits({
        page: currentPage,
        limit: itemsPerPage
      });
      if (response.success && Array.isArray(response.data)) {
        const auditsData = response.data.map(audit => ({
          ...audit,
          days_remaining: calculateDaysRemaining(audit.due_date),
          is_overdue: new Date(audit.due_date) < new Date() && audit.status !== 'approved'
        }));
        setAudits(auditsData);
        calculateStats(auditsData);
        
        // Update pagination info
        const responseWithPagination = response as any;
        if (responseWithPagination.pagination) {
          setTotalPages(responseWithPagination.pagination.pages);
          setTotalItems(responseWithPagination.pagination.total);
        }
      } else {
        setAudits([]);
      }
    } catch (error) {
      console.error('Failed to fetch my audits:', error);
      setAudits([]);
    } finally {
      setLoading(false);
    }
  };

  const calculateDaysRemaining = (dueDate: string): number => {
    const today = new Date();
    const due = new Date(dueDate);
    const diffTime = due.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getAuditorDisplayInfo = (audit: MyAudit) => {
    if (audit.multi_auditor_support && audit.multi_auditor_info) {
      const auditorInfo = audit.multi_auditor_info.split('|').map(info => {
        const [auditorId, role, status] = info.split(':');
        return { auditorId, role, status };
      });
      
      const userInfo = auditorInfo.find(info => info.auditorId === user?.id?.toString());
      if (userInfo) {
        return {
          isMultiAuditor: true,
          userRole: userInfo.role,
          totalAuditors: auditorInfo.length,
          userStatus: userInfo.status
        };
      }
    }
    
    return {
      isMultiAuditor: false,
      userRole: 'primary',
      totalAuditors: 1,
      userStatus: audit.status
    };
  };

  const calculateStats = (auditsData: MyAudit[]) => {
    const newStats = {
      total: auditsData.length,
      assigned: auditsData.filter(a => a.status === 'assigned').length,
      in_progress: auditsData.filter(a => a.status === 'in_progress').length,
      submitted: auditsData.filter(a => a.status === 'submitted').length,
      approved: auditsData.filter(a => a.status === 'approved').length,
      overdue: auditsData.filter(a => a.is_overdue).length
    };
    setStats(newStats);
  };

  const filteredAudits = audits.filter(audit => {
    const matchesSearch = 
      audit.patient_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      audit.patient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      audit.primary_diagnosis?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      audit.case_summary?.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || audit.status === statusFilter;
    const matchesPriority = priorityFilter === 'all' || audit.priority === priorityFilter;
    const matchesType = typeFilter === 'all' || audit.case_type === typeFilter;

    return matchesSearch && matchesStatus && matchesPriority && matchesType;
  });

  const sortedAudits = [...filteredAudits].sort((a, b) => {
    switch (sortBy) {
      case 'due_date':
        return new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
      case 'priority':
        const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
        return priorityOrder[b.priority] - priorityOrder[a.priority];
      case 'assigned_date':
        return new Date(b.assigned_date).getTime() - new Date(a.assigned_date).getTime();
      case 'status':
        return a.status.localeCompare(b.status);
      default:
        return 0;
    }
  });

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      assigned: { color: 'bg-blue-100 text-blue-800', icon: Clock },
      in_progress: { color: 'bg-yellow-100 text-yellow-800', icon: Activity },
      submitted: { color: 'bg-purple-100 text-purple-800', icon: FileText },
      under_review: { color: 'bg-orange-100 text-orange-800', icon: Eye },
      needs_revision: { color: 'bg-amber-100 text-amber-800', icon: RefreshCw },
      approved: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      rejected: { color: 'bg-red-100 text-red-800', icon: AlertCircle }
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.assigned;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const getPriorityBadge = (priority: string) => {
    const priorityConfig = {
      low: { color: 'bg-gray-100 text-gray-800', icon: Flag },
      medium: { color: 'bg-blue-100 text-blue-800', icon: Flag },
      high: { color: 'bg-orange-100 text-orange-800', icon: Flag },
      urgent: { color: 'bg-red-100 text-red-800', icon: Flag }
    };

    const config = priorityConfig[priority as keyof typeof priorityConfig] || priorityConfig.medium;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3 mr-1" />
        {priority.toUpperCase()}
      </span>
    );
  };

  const handleShowDetails = (audit: MyAudit) => {
    setSelectedAudit(audit);
    setShowDetailsModal(true);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (itemsPerPage: number) => {
    setItemsPerPage(itemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const getActionButton = (audit: MyAudit) => {
    switch (audit.status) {
      case 'assigned':
        return (
          <button
            onClick={() => navigate(`/audit/${audit.id}`)}
            className="btn btn-primary btn-sm"
          >
            <Play className="h-4 w-4 mr-1" />
            Start Audit
          </button>
        );
      case 'in_progress':
        return (
          <button
            onClick={() => navigate(`/audit/${audit.id}`)}
            className="btn btn-secondary btn-sm"
          >
            <Edit className="h-4 w-4 mr-1" />
            Continue
          </button>
        );
      case 'submitted':
      case 'under_review':
        return (
          <button
            onClick={() => navigate(`/audit/${audit.id}`)}
            className="btn btn-outline btn-sm"
          >
            <Eye className="h-4 w-4 mr-1" />
            View
          </button>
        );
      case 'approved':
        return (
          <button
            onClick={() => navigate(`/audit/${audit.id}`)}
            className="btn btn-outline btn-sm"
          >
            <CheckCircle className="h-4 w-4 mr-1" />
            View
          </button>
        );
      case 'rejected':
        return (
          <button
            onClick={() => navigate(`/audit/${audit.id}`)}
            className="btn btn-danger btn-sm"
          >
            <Edit className="h-4 w-4 mr-1" />
            Revise
          </button>
        );
      default:
        return null;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 px-8 py-6">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">My Audits</h1>
              <p className="text-blue-100">Manage your assigned audit cases and track progress</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-sm font-medium text-gray-600">Total Audits</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center">
              <Clock className="w-6 h-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">{stats.assigned}</p>
              <p className="text-sm font-medium text-gray-600">Assigned</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-yellow-100 rounded-2xl flex items-center justify-center">
              <Activity className="w-6 h-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">{stats.in_progress}</p>
              <p className="text-sm font-medium text-gray-600">In Progress</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">{stats.submitted}</p>
              <p className="text-sm font-medium text-gray-600">Submitted</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">{stats.approved}</p>
              <p className="text-sm font-medium text-gray-600">Approved</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-red-100 rounded-2xl flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-2xl font-bold text-gray-900">{stats.overdue}</p>
              <p className="text-sm font-medium text-gray-600">Overdue</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          <div className="flex flex-col sm:flex-row gap-4 items-center">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search audits..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Status</option>
              <option value="assigned">Assigned</option>
              <option value="in_progress">In Progress</option>
              <option value="submitted">Submitted</option>
              <option value="under_review">Under Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>

            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Priority</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>

            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Types</option>
              <option value="mortality">Mortality</option>
              <option value="morbidity">Morbidity</option>
            </select>
          </div>

          <div className="flex items-center gap-3">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="due_date">Sort by Due Date</option>
              <option value="priority">Sort by Priority</option>
              <option value="assigned_date">Sort by Assigned Date</option>
              <option value="status">Sort by Status</option>
            </select>

            <button
              onClick={fetchMyAudits}
              disabled={loading}
              className="btn btn-outline"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Audits List */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-3 text-gray-600">Loading your audits...</span>
          </div>
        ) : sortedAudits.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No audits found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || statusFilter !== 'all' || priorityFilter !== 'all' || typeFilter !== 'all'
                ? 'Try adjusting your filters to see more results.'
                : 'You don\'t have any audit assignments yet.'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {sortedAudits.map((audit) => (
              <div key={audit.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {audit.patient_id} - {audit.patient_name || 'Unknown Patient'}
                      </h3>
                      {getStatusBadge(audit.status)}
                      {getPriorityBadge(audit.priority)}
                      {(() => {
                        const auditorInfo = getAuditorDisplayInfo(audit);
                        if (auditorInfo.isMultiAuditor) {
                          return (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                              <Users className="w-3 h-3 mr-1" />
                              {auditorInfo.userRole.toUpperCase()} ({auditorInfo.totalAuditors} auditors)
                            </span>
                          );
                        }
                        return null;
                      })()}
                      {audit.is_overdue && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <AlertCircle className="w-3 h-3 mr-1" />
                          OVERDUE
                        </span>
                      )}
                      {audit.status === 'needs_revision' && audit.revision_count && audit.revision_count > 0 && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                          <RefreshCw className="w-3 h-3 mr-1" />
                          REVISION {audit.revision_count}
                        </span>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                      <div className="flex items-center text-sm text-gray-600">
                        <CalendarIcon className="h-4 w-4 mr-2 text-gray-400" />
                        <span>Due: {formatDate(audit.due_date)}</span>
                        {audit.days_remaining !== undefined && (
                          <span className={`ml-2 px-2 py-1 rounded-full text-xs ${
                            audit.days_remaining < 0 
                              ? 'bg-red-100 text-red-800' 
                              : audit.days_remaining <= 3 
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-green-100 text-green-800'
                          }`}>
                            {audit.days_remaining < 0 
                              ? `${Math.abs(audit.days_remaining)} days overdue`
                              : `${audit.days_remaining} days left`
                            }
                          </span>
                        )}
                      </div>

                      <div className="flex items-center text-sm text-gray-600">
                        <User className="h-4 w-4 mr-2 text-gray-400" />
                        <span>{audit.case_type.charAt(0).toUpperCase() + audit.case_type.slice(1)} Case</span>
                      </div>

                      <div className="flex items-center text-sm text-gray-600">
                        <BarChart3 className="h-4 w-4 mr-2 text-gray-400" />
                        <span>{audit.department_name || 'Unknown Department'}</span>
                      </div>

                      <div className="flex items-center text-sm text-gray-600">
                        <Activity className="h-4 w-4 mr-2 text-gray-400" />
                        <span>{audit.unit_name || 'Unknown Unit'}</span>
                      </div>
                    </div>

                    {audit.primary_diagnosis && (
                      <p className="text-sm text-gray-600 mb-2">
                        <strong>Diagnosis:</strong> {audit.primary_diagnosis}
                      </p>
                    )}

                    {audit.case_summary && (
                      <p className="text-sm text-gray-600 mb-3">
                        <strong>Summary:</strong> {audit.case_summary}
                      </p>
                    )}

                    {/* Progress Bar */}
                    <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                      <div 
                        className={`h-2 rounded-full transition-all duration-300 ${
                          audit.progress_percentage >= 100 
                            ? 'bg-green-500' 
                            : audit.progress_percentage >= 50 
                              ? 'bg-blue-500' 
                              : 'bg-yellow-500'
                        }`}
                        style={{ width: `${Math.min(audit.progress_percentage, 100)}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-500">
                      {audit.progress_percentage}% Complete
                    </p>
                  </div>

                  <div className="ml-6 flex flex-col items-end space-y-2">
                    {getActionButton(audit)}
                    <button
                      onClick={() => handleShowDetails(audit)}
                      className="btn btn-outline btn-sm"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Pagination */}
        {sortedAudits.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            showItemsPerPage={true}
            itemsPerPageOptions={[10, 20, 50, 100]}
          />
        )}
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedAudit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Audit Details</h2>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Basic Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Basic Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Patient ID</label>
                      <p className="text-gray-900">{selectedAudit.patient_id}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Case Type</label>
                      <p className="text-gray-900 capitalize">{selectedAudit.case_type}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Status</label>
                      <div className="mt-1">{getStatusBadge(selectedAudit.status)}</div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Priority</label>
                      <div className="mt-1">{getPriorityBadge(selectedAudit.priority)}</div>
                    </div>
                  </div>
                </div>

                {/* Assignment Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Assignment Information</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Assigned Date</label>
                      <p className="text-gray-900">{new Date(selectedAudit.assigned_date).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Due Date</label>
                      <p className="text-gray-900">{new Date(selectedAudit.due_date).toLocaleDateString()}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Department</label>
                      <p className="text-gray-900">{selectedAudit.department_name || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Unit</label>
                      <p className="text-gray-900">{selectedAudit.unit_name || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                {/* Case Information */}
                {(selectedAudit.case_summary || selectedAudit.primary_diagnosis) && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Case Information</h3>
                    <div className="space-y-3">
                      {selectedAudit.case_summary && (
                        <div>
                          <label className="text-sm font-medium text-gray-500">Case Summary</label>
                          <p className="text-gray-900 mt-1">{selectedAudit.case_summary}</p>
                        </div>
                      )}
                      {selectedAudit.primary_diagnosis && (
                        <div>
                          <label className="text-sm font-medium text-gray-500">Primary Diagnosis</label>
                          <p className="text-gray-900 mt-1">{selectedAudit.primary_diagnosis}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Progress Information */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Progress Information</h3>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-sm font-medium text-gray-500 mb-1">
                        <span>Progress</span>
                        <span>{selectedAudit.progress_percentage}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${selectedAudit.progress_percentage}%` }}
                        ></div>
                      </div>
                    </div>
                    {selectedAudit.days_remaining !== undefined && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Days Remaining</label>
                        <p className={`text-sm font-medium ${selectedAudit.is_overdue ? 'text-red-600' : 'text-gray-900'}`}>
                          {selectedAudit.days_remaining} days {selectedAudit.is_overdue ? '(Overdue)' : ''}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Notes */}
                {selectedAudit.notes && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Notes</h3>
                    <p className="text-gray-900">{selectedAudit.notes}</p>
                  </div>
                )}
              </div>

              {/* Modal Actions */}
              <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    navigate(`/audit/${selectedAudit.id}`);
                  }}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  View Full Audit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
